/**
 * Bootstrap API
 *
 * API client for the bootstrap endpoints that determine post-login routing.
 */

import { axiosInstance } from '@/shared/lib/axios';
import type {
  BootstrapResponse,
  IsAdminResponse,
  SoloStatusResponse,
  ShopStatusResponse,
} from './bootstrap.types';

const BASE_URL = '/api/v1/me';

const withAuthHeader = (token?: string | null) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

export const bootstrap = {
  /**
   * Get bootstrap data for post-login routing
   * Returns user type, provider profile status, and shop memberships
   */
  getBootstrapData: async (token?: string | null): Promise<BootstrapResponse> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/bootstrap`,
      withAuthHeader(token),
    );
    return response.data;
  },

  /**
   * Get solo provider status
   * Used by SoloLayout to check subscription and setup state
   */
  getSoloStatus: async (token?: string | null): Promise<SoloStatusResponse> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/solo-status`,
      withAuthHeader(token),
    );
    return response.data;
  },

  /**
   * Get shop status for a specific shop
   * Used by ShopLayout to check membership and subscription
   */
  getShopStatus: async (
    shopSlug: string,
    token?: string | null,
  ): Promise<ShopStatusResponse> => {
    const response = await axiosInstance.get(
      `/api/v1/shops/${shopSlug}/status`,
      withAuthHeader(token),
    );
    return response.data;
  },

  /**
   * Complete user onboarding (role selection)
   * Called when user selects "Book Appointments" (client role)
   */
  completeOnboarding: async (): Promise<{ success: boolean }> => {
    const response = await axiosInstance.post(`${BASE_URL}/complete-onboarding`);
    return response.data;
  },

  /**
   * Probe whether the authenticated user is an admin.
   * Used by post-login routing; AdminGuard keeps using /admin/session.
   * Returns { isAdmin: false } for non-admins instead of 403.
   */
  getIsAdmin: async (token?: string | null): Promise<IsAdminResponse> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/is-admin`,
      withAuthHeader(token),
    );
    return response.data;
  },
};
